import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ViewChild, TemplateRef } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { IrbSettingsService } from "@irb/irb-settings/irb-settings.service";
import { IGlobalSettings, IGlobalSettingsSaveResponse } from "../irb-settings.model";
import { Subscription, Observable, of } from "rxjs";
import { AutoToleranceDropDown, UIDropDownDTO } from "./settings-models-rebalancing.model";
import { UtilityService } from "@irb/irb-common/services/utility.service";
import { AppService } from "@irb/app.service";
import { PageService } from "@irb/irb-common/services/page.service";
import { IEvent, IrbSettingsEvents, IrbAppEvents } from "@irb-common/events";
import { ValidationService } from "@irb/irb-common/services/validation.service";
import { SettingsPage } from "@irb/irb-settings/irb-settings.page";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { IrbAuthenticationService } from "@irb/irb-authentication/irb-authentication.service";
import { IAlertDialogData } from "@irb/app.model";
import {InventoryService} from "@irb/irb-orders/order-blotter/inventory/inventory.service";
import {AnalyticsService} from "@irb-common/analytics/analytics.service";
import {DropdownFilterComponent} from "@irb-common/components/dropdown-filter/dropdown-filter.component";

@Component({ standalone: false,
  selector: "irb-settings-models-rebalancing",
  templateUrl: "./settings-models-rebalancing.component.html",
  styleUrls: ["./settings-models-rebalancing.component.scss", "../../irb-common/styles/dialog.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsModelsRebalancingComponent implements SettingsPage<IGlobalSettings>, OnInit, OnDestroy {
  @ViewChild("autoToleranceMinPerc") autoToleranceMinPerc: any;
  @ViewChild("autoToleranceMaxPerc") autoToleranceMaxPerc: any;
  @ViewChild("securityTradeCorrectionRangePerc") securityTradeCorrectionRangePerc: any;
  @ViewChild("cashTradeCorrectionRangePerc") cashTradeCorrectionRangePerc: any;
  @ViewChild("IrbAutoToleranceDropDown") IrbAutoToleranceDropDown: any;
  _globalSettings: IGlobalSettings;
  originalGlobalSettings: IGlobalSettings;
  _isModified: boolean;
  eventSubscription: Subscription;
  showUnsavedChangesWarning: boolean = false;
  _autoToleranceDropDown: AutoToleranceDropDown;
  faCaretDown = faCaretDown;
  autoToleranceApplicableTo: any;
  isAutoToleranceApplicableTo: boolean = false;
  _autoToleranceSetting: UIDropDownDTO;
  defaultExpandedPanels: Array<string> = ["HISTORY","REBALANCE_BANDS", "REBAL_ACCURACY_RANGE"];
  isadmin: boolean = false;
  validationErrors: string[];
  validationErrorTemplates: TemplateRef<any>[];
  ENABLE_SETTINGS_HIST = false;
  ENABLE_CASH_ACCURACY = false;

  constructor(public iss: IrbSettingsService, private cdr: ChangeDetectorRef, private util: UtilityService,
    private as: AppService, private ps: PageService, private vs: ValidationService, public ias: IrbAuthenticationService,
    public ats: AnalyticsService) {
    this.ats.trackGlobalSettingsModelsEvent();
    this.eventSubscription = this.as.getEvents().subscribe((s) => {
      this.processEvent(s);
    });
    this.ENABLE_SETTINGS_HIST = this.as.getItem("IRB_APP_CONFIG")?.featureFlags?.ENABLE_SETTINGS_HIST || false;
    this.ENABLE_CASH_ACCURACY = this.as.getItem("IRB_APP_CONFIG")?.featureFlags?.ENABLE_CASH_ACCURACY || false;
  }

  ngOnInit() {
    this.ias.getAppConfigDetails().subscribe((data: any) => {
      this.isadmin = data.isAdmin;
    });
    // this.isadmin=false;

    this.ps.deserializePageState(this.as.getFromSession("pageState"), ["modelsGridDiv"]);
    if (!this.ENABLE_SETTINGS_HIST) {
      this.defaultExpandedPanels = this.defaultExpandedPanels.filter(p => p !== 'HISTORY');
    }
    this.iss.loadExpansionState(this.defaultExpandedPanels.length, "settings-models-rebalancing", this.defaultExpandedPanels);
    if (this.ENABLE_SETTINGS_HIST) {
      this.iss.updateExpansionState('HISTORY','closed');
    }
    this.cdr.detectChanges();

    this.iss.getAutoToleranceDropDown().subscribe((data: any) => {
      this.loadAutoToleranceDropDown(data as AutoToleranceDropDown);

      this.iss.getGlobalSettings().subscribe((data: any) => {
        this.load(data as IGlobalSettings);
      });
    });
  }

  setAutoToleranceDropDown(searchStr: any) {
    let autoToleranceDD: any = [],
      newItem: any;
    const f = DropdownFilterComponent.getFilteredList(searchStr,this.autoToleranceDropDown.items);
      f.forEach((item: any) => {
      newItem = {
        name: item.name,
        value: item.id,
      };
      autoToleranceDD.push(newItem);
    });
    this.IrbAutoToleranceDropDown.set(autoToleranceDD);
  }

  onAutoRebalance() {
    this.globalSettings.autoToleranceApplicableTo = this.autoToleranceSetting.name;

    if (this.isAutoToleranceApplicableTo) {
      if (this.globalSettings.autoToleranceApplicableTo !== "None") {
        this.onInputChange();
      }
    } else {
      this.onInputChange();
    }
  }

  ngOnDestroy() {
    this.eventSubscription.unsubscribe();
  }

  loadAutoToleranceDropDown(data: AutoToleranceDropDown) {
    this.autoToleranceDropDown = data;
    this.cdr.detectChanges();
  }

  load(data: IGlobalSettings) {
    this.autoToleranceSetting = {
      name: "Apply to current and future model targets that rely on global rebalancing bands",
      id: "EXISTING_MODELS",
      value: "EXISTING_MODELS",
      versionId: null,
    } as UIDropDownDTO;
    this.globalSettings = data;
    this.globalSettings.currentSetting = "modelView";

    if(this.globalSettings.cashTradeCorrectionRangePerc == null)
    {
      this.globalSettings.cashTradeCorrectionRangePerc = 0;
    }

    this.globalSettings.autoToleranceApplicableTo = this.autoToleranceSetting.name;
    this.originalGlobalSettings = this.util.clone(this.globalSettings);
    this.isModified = false;
    this.as.fireEvent(IrbSettingsEvents.IS_NOT_MODIFIED);

    this.cdr.detectChanges();
  }

  saveAllModels() {
    this.validateInput().subscribe(validationErrors => {
      if(validationErrors.length === 0) {
        if (this.autoToleranceSetting.name === "Apply to all model targets") {
          this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
            dTitle: "Warning",
            altMsg: "Continuing with this action will reset the rebalancing bands for all model targets in this system using the values you have entered. Bands that you have previously overridden will also be reset. Press continue to proceed, or cancel to stop this action.",
            dProceedBtnLabel: "Continue",
            dCancelBtnLabel: "Cancel",
            dType: "WARN",
            onProceed: (() => {
              this.save();
            }).bind(this)
          } as IAlertDialogData);
        } else {
          this.save();
        }
      } else {
        this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
          severity: "error",
          summary: "Save",
          detail: "Please correct invalid entries before saving."
        });
      }
    });
  }

  save() {
    this.as.fireEvent(IrbSettingsEvents.SAVE_START);
    this.iss.saveModelsGlobalSettings(this.globalSettings).subscribe(
      (data: any) => {
        const response = data as IGlobalSettingsSaveResponse;
        if (response.responseStatus === "SUCCESS") {
            this.iss.getGlobalSettings().subscribe((data: any) => {
              this.as.fireEvent(IrbSettingsEvents.SAVE_SUCCESS, "Models settings were updated.");
              this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
              this.load(data as IGlobalSettings);
            });
        } else {
          this.as.fireEvent(IrbSettingsEvents.SAVE_ERROR);
        }
      },
      () => {
        this.as.fireEvent(IrbSettingsEvents.SAVE_ERROR);
      }
    );
  }

  revert() {
    if (this.isModified) {
      this.load(this.originalGlobalSettings);
    }
  }

  processEvent(event: IEvent) {
    switch (event.type) {
      case IrbSettingsEvents.SAVE:
        this.save();
        break;
      case IrbSettingsEvents.UNSAVED_CHANGES:
        if (this.isModified) {
          this.showUnsavedChangesWarning = true;
          this.cdr.detectChanges();
        }
        break;
      case IrbSettingsEvents.REVERT_CHANGES:
        this.revert();
        break;
    }
  }

  closeWithoutSave() {
    this.as.fireEvent(IrbSettingsEvents.CLOSE_WITHOUT_SAVE);
  }

  onInputChange() {
    window.setTimeout(() => {
      this.isModified = !this.util.compare(this.originalGlobalSettings, this.globalSettings);
      this.as.fireEvent(this.isModified ? IrbSettingsEvents.IS_MODIFIED : IrbSettingsEvents.IS_NOT_MODIFIED);
      //this.showUnsavedChangesWarning = false;
      this.validateInput().subscribe(_validationErrors => {});
      this.cdr.detectChanges();
    });
  }

  validateInput(): Observable<string[]> {
    this.validationErrors = [];
    this.validationErrorTemplates = [];
    
    if (!this.autoToleranceMinPerc?.inputControl.valid) {
      this.validationErrors.push('');
    }

    if (this.autoToleranceMinPerc?.value === null || this.autoToleranceMinPerc?.value === undefined) {
      this.validationErrors.push('');
    }

    if (this.autoToleranceMaxPerc?.value === null || this.autoToleranceMaxPerc?.value === undefined) {
      this.validationErrors.push('');
    }

    if (!this.autoToleranceMaxPerc?.inputControl.valid) {
      this.validationErrors.push('');
    }

    if (!this.securityTradeCorrectionRangePerc?.inputControl.valid) {
      this.validationErrors.push('');
    }

    if (this.cashTradeCorrectionRangePerc && !this.cashTradeCorrectionRangePerc.inputControl.valid) {
      this.validationErrors.push('');
    }

    return of(this.validationErrors);
  }

  get globalSettings() {
    return this._globalSettings;
  }

  set globalSettings(newGlobalSettings: IGlobalSettings) {
    this._globalSettings = newGlobalSettings;
  }

  get isModified() {
    return this._isModified;
  }

  set isModified(newIsModified: boolean) {
    this._isModified = newIsModified;
  }

  get autoToleranceDropDown() {
    return this._autoToleranceDropDown;
  }

  set autoToleranceDropDown(newAutoToleranceDropDown: AutoToleranceDropDown) {
    this._autoToleranceDropDown = newAutoToleranceDropDown;
  }

  get autoToleranceSetting(): UIDropDownDTO {
    return this._autoToleranceSetting;
  }

  set autoToleranceSetting(d: UIDropDownDTO) {
    this._autoToleranceSetting = d;
  }

}
